import React from 'react';
import { hydrateRemoteImages } from '../../utils/remoteImages';
import { useRemoteImages } from '../RemoteImages';

interface OriginalEmailFrameProps {
    html: string;
    className?: string;
}

const IFRAME_SANDBOX = [
    'allow-same-origin',
    'allow-popups',
    'allow-popups-to-escape-sandbox',
    'allow-top-navigation-by-user-activation',
].join(' ');

const MIN_FRAME_HEIGHT = 160;
/** Below this scale, prefer horizontal scroll over an unreadable shrink. */
const MIN_SCALE = 0.6;
/** Extra room under content so the last line is not clipped. */
const HEIGHT_PADDING = 16;

type FrameMetrics = {
    height: number;
    width: number;
};

/**
 * Measure iframe document size after layout (images may still be loading).
 * Resets any previous scale transform so measurements stay accurate.
 *
 * Important: use scrollHeight/scrollWidth only. offsetHeight/offsetWidth track the
 * iframe viewport, so max(scroll, offset) + padding + ResizeObserver on the iframe
 * itself grows the frame forever (each cycle: H → max(content, H) + pad).
 */
const measureFrameContent = (iframe: HTMLIFrameElement): FrameMetrics => {
    const doc = iframe.contentDocument;
    if (!doc)
        return { height: MIN_FRAME_HEIGHT, width: 0 };

    const body = doc.body;
    const root = doc.documentElement;
    if (body) {
        body.style.transform = '';
        body.style.transformOrigin = '';
    }
    if (root) {
        root.style.overflowX = 'hidden';
        root.style.overflowY = 'hidden';
    }
    if (body) {
        body.style.overflowX = 'hidden';
        body.style.overflowY = 'hidden';
    }

    const height = Math.max(
        body?.scrollHeight || 0,
        root?.scrollHeight || 0,
        MIN_FRAME_HEIGHT
    );
    const width = Math.max(
        body?.scrollWidth || 0,
        root?.scrollWidth || 0,
        0
    );
    return { height: height + HEIGHT_PADDING, width };
};

/**
 * Fit wide newsletter HTML into the iframe width.
 * Scales down when content overflows; falls back to horizontal scroll under MIN_SCALE.
 */
const applyFitToWidth = (
    iframe: HTMLIFrameElement,
    metrics: FrameMetrics
): number => {
    const doc = iframe.contentDocument;
    const body = doc?.body;
    const root = doc?.documentElement;
    const frameWidth = iframe.clientWidth || 0;
    const contentWidth = metrics.width;
    const contentHeight = metrics.height;

    if (!body || !root || frameWidth <= 0 || contentWidth <= frameWidth) {
        if (root) {
            root.style.overflowX = 'hidden';
            root.style.overflowY = 'hidden';
        }
        if (body) {
            body.style.overflowX = 'hidden';
            body.style.overflowY = 'hidden';
        }
        return contentHeight;
    }

    const scale = frameWidth / contentWidth;
    if (scale < MIN_SCALE) {
        // Too small to read when scaled — allow horizontal scroll instead.
        body.style.transform = '';
        body.style.transformOrigin = '';
        root.style.overflowX = 'auto';
        root.style.overflowY = 'hidden';
        body.style.overflowX = 'auto';
        body.style.overflowY = 'hidden';
        return contentHeight;
    }

    body.style.transformOrigin = 'top left';
    body.style.transform = `scale(${scale})`;
    root.style.overflowX = 'hidden';
    root.style.overflowY = 'hidden';
    body.style.overflowX = 'hidden';
    body.style.overflowY = 'hidden';
    return Math.max(MIN_FRAME_HEIGHT, Math.ceil(contentHeight * scale));
};

/**
 * Sandboxed preview of scrubbed original email HTML.
 * Remote images hydrate only when the card session allows it.
 * Wide marketing layouts scale to fit (or scroll horizontally under MIN_SCALE).
 * Height grows with (scaled) content so the frame does not need a vertical scrollbar.
 */
const OriginalEmailFrame = (props: OriginalEmailFrameProps) => {
    const { html, className } = props;
    const { showRemoteImages } = useRemoteImages();
    const iframeRef = React.useRef<HTMLIFrameElement | null>(null);
    const heightRef = React.useRef(MIN_FRAME_HEIGHT);
    const widthRef = React.useRef(0);
    const rafRef = React.useRef<number | null>(null);
    const timersRef = React.useRef<number[]>([]);
    const [frameHeight, setFrameHeight] = React.useState(MIN_FRAME_HEIGHT);

    const srcDoc = React.useMemo(() => {
        const body = showRemoteImages ? hydrateRemoteImages(html || '') : (html || '');
        // Emails are authored for light backgrounds; keep a light canvas in both themes
        // so message text stays readable (do not inherit app dark:text).
        return `<!DOCTYPE html><html><head><meta charset="utf-8">`
            + `<base target="_blank" rel="noopener noreferrer">`
            + `<style>`
            + `html,body{margin:0;padding:8px;background:#fff;color:#111827;overflow-x:hidden;overflow-y:hidden;}`
            + `img{max-width:100%;height:auto;display:block;}`
            + `a{color:#1d4ed8;}`
            + `</style></head>`
            + `<body>${body}</body></html>`;
    }, [html, showRemoteImages]);

    const clearScheduled = React.useCallback(() => {
        if (rafRef.current != null) {
            window.cancelAnimationFrame(rafRef.current);
            rafRef.current = null;
        }
        timersRef.current.forEach((id) => window.clearTimeout(id));
        timersRef.current = [];
    }, []);

    const syncLayout = React.useCallback(() => {
        const iframe = iframeRef.current;
        if (!iframe)
            return;

        if (rafRef.current != null)
            window.cancelAnimationFrame(rafRef.current);

        rafRef.current = window.requestAnimationFrame(() => {
            rafRef.current = null;
            try {
                const metrics = measureFrameContent(iframe);
                const next = applyFitToWidth(iframe, metrics);
                // Ignore sub-pixel / 1px thrashing that feeds error overlays.
                if (Math.abs(next - heightRef.current) < 2)
                    return;
                heightRef.current = next;
                setFrameHeight(next);
            } catch {
                heightRef.current = 480;
                setFrameHeight(480);
            }
        });
    }, []);

    const onLoad = React.useCallback(() => {
        const iframe = iframeRef.current;
        if (!iframe)
            return;

        clearScheduled();
        widthRef.current = iframe.clientWidth || 0;
        syncLayout();

        try {
            const doc = iframe.contentDocument;
            if (!doc)
                return;

            const onImageSettled = () => syncLayout();
            Array.from(doc.images || []).forEach((img) => {
                if (img.complete)
                    return;
                img.addEventListener('load', onImageSettled, { once: true });
                img.addEventListener('error', onImageSettled, { once: true });
            });

            // Late layout after fonts / deferred images.
            timersRef.current = [
                window.setTimeout(syncLayout, 50),
                window.setTimeout(syncLayout, 250),
                window.setTimeout(syncLayout, 1000),
            ];
        } catch {
            // Sandbox / cross-origin — keep last measured height.
        }
    }, [clearScheduled, syncLayout]);

    React.useEffect(() => {
        heightRef.current = MIN_FRAME_HEIGHT;
        widthRef.current = 0;
        setFrameHeight(MIN_FRAME_HEIGHT);
        return () => clearScheduled();
    }, [srcDoc, clearScheduled]);

    // Re-fit only when the iframe *width* changes (modal resize / sidebar).
    // Do not react to our own height updates — that re-enters syncLayout forever.
    // Defer out of the RO callback — sync setState inside RO trips CRA's overlay.
    React.useEffect(() => {
        const iframe = iframeRef.current;
        if (!iframe || typeof ResizeObserver === 'undefined')
            return undefined;

        let timeoutId: number | null = null;
        const observer = new ResizeObserver((entries) => {
            const entry = entries[0];
            const nextWidth = entry?.contentRect?.width
                ?? iframe.clientWidth
                ?? 0;
            if (Math.abs(nextWidth - widthRef.current) < 1)
                return;
            widthRef.current = nextWidth;
            if (timeoutId != null)
                window.clearTimeout(timeoutId);
            timeoutId = window.setTimeout(syncLayout, 0);
        });
        observer.observe(iframe);
        return () => {
            observer.disconnect();
            if (timeoutId != null)
                window.clearTimeout(timeoutId);
        };
    }, [syncLayout, srcDoc]);

    return (
        <iframe
            ref={iframeRef}
            title="Original email"
            className={className || 'w-full max-w-full border border-gray-200 dark:border-gray-700 rounded bg-white dark:bg-gray-800'}
            sandbox={IFRAME_SANDBOX}
            srcDoc={srcDoc}
            onLoad={onLoad}
            style={{
                minHeight: MIN_FRAME_HEIGHT,
                height: frameHeight,
                overflow: 'hidden',
                display: 'block',
                border: 0,
            }}
        />
    );
};

export default OriginalEmailFrame;
